import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Title,
  Text,
  Loader,
  Alert,
  Stack,
  Box,
  NavLink,
  Paper,
  Group,
  Badge,
  Drawer,
  Button,
  ActionIcon,
  Divider,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconCategory,
  IconFilter,
  IconX,
  IconChevronRight,
  IconPackage,
} from '@tabler/icons-react';
import { useProducts, useCategories } from '@/lib/hooks';
import { ProductCard } from '@/components/ProductCard';
import { useLanguage } from '@/context/LanguageContext';
import { useSEO } from '@/lib/useSEO';
import { Category, Product } from '@/types';

export default function ProductsPage() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category') || null;
  const [filterOpened, { open: openFilter, close: closeFilter }] = useDisclosure(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { data: productsData, isLoading: productsLoading, error: productsError } = useProducts();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();

  const products = productsData?.products ?? [];
  const categories = categoriesData?.categories ?? [];

  // Filter products by selected category
  const filteredProducts = useMemo(() => {
    if (!selectedCategory) return products;
    return products.filter((product: Product) => product.category === selectedCategory);
  }, [products, selectedCategory]);

  // Get product count per category
  const categoryProductCount = useMemo(() => {
    const counts: Record<string, number> = {};
    products.forEach((product: Product) => {
      if (product.category) {
        counts[product.category] = (counts[product.category] || 0) + 1;
      }
    });
    return counts;
  }, [products]);

  const handleCategorySelect = (categorySlug: string | null) => {
    if (categorySlug) {
      setSearchParams({ category: categorySlug });
    } else {
      setSearchParams({});
    }
    closeFilter();
  };

  const selectedCategoryName = useMemo(() => {
    if (!selectedCategory) return null;
    const category = categories.find((c: Category) => c.slug === selectedCategory);
    return category?.name || selectedCategory;
  }, [selectedCategory, categories]);

  // SEO for Products page - updates based on selected category
  const seoTitle = selectedCategoryName 
    ? `${selectedCategoryName} - ${t('seo.products.title') || 'Products'} | Luxelo`
    : t('seo.products.title') || 'Products - Luxelo';
  const seoDescription = selectedCategoryName
    ? `Browse our ${selectedCategoryName} collection. High-quality LED lighting products with fast shipping.`
    : t('seo.products.description') || 'Browse our complete collection of premium LED lighting products.';

  useSEO({
    title: seoTitle,
    description: seoDescription,
    type: 'website',
  });

  const CategoriesSidebar = () => (
    <Stack gap="xs">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <IconCategory size={20} stroke={1.5} />
          <Text fw={600} size="sm" tt="uppercase" c="dimmed">
            {t('products.categories')}
          </Text>
        </Group>
        {isMobile && (
          <ActionIcon variant="subtle" onClick={closeFilter}>
            <IconX size={18} />
          </ActionIcon>
        )}
      </Group>

      <NavLink
        label={t('products.allProducts')}
        active={selectedCategory === null}
        onClick={() => handleCategorySelect(null)}
        rightSection={
          <Badge size="sm" variant="light" color="gray">
            {products.length}
          </Badge>
        }
        styles={{
          root: {
            borderRadius: 8,
            fontWeight: selectedCategory === null ? 600 : 400,
          },
        }}
      />

      <Divider my="xs" />

      {categoriesLoading ? (
        <Box py="md" ta="center">
          <Loader size="sm" />
        </Box>
      ) : categories.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          {t('products.noCategories')}
        </Text>
      ) : (
        categories.map((category: Category) => (
          <NavLink
            key={category.id}
            label={category.name}
            description={category.description}
            active={selectedCategory === category.slug}
            onClick={() => handleCategorySelect(category.slug)}
            rightSection={
              <Group gap={4}>
                <Badge size="sm" variant="light" color="gray">
                  {categoryProductCount[category.slug] || 0}
                </Badge>
                <IconChevronRight size={14} stroke={1.5} />
              </Group>
            }
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: selectedCategory === category.slug ? 600 : 400,
              },
            }}
          />
        ))
      )}
    </Stack>
  );

  if (productsLoading) {
    return (
      <Container py="xl">
        <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
      </Container>
    );
  }

  if (productsError) {
    return (
      <Container py="xl">
        <Alert color="red" title={t('common.error')}>
          {t('products.loadError')}
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Mobile filter drawer */}
      <Drawer
        opened={filterOpened}
        onClose={closeFilter}
        title={
          <Group gap="xs">
            <IconFilter size={20} />
            <Text fw={600}>{t('products.filterBy')}</Text>
          </Group>
        }
        position="left"
        size="280px"
        padding="md"
      >
        <CategoriesSidebar />
      </Drawer>

      <Container size="xl" py="xl">
        {/* Page header */}
        <Stack gap="md" mb="xl">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title order={1} mb="xs">
                {selectedCategoryName || t('products.title')}
              </Title>
              <Text c="dimmed" size="lg">
                {selectedCategory
                  ? t('products.categoryResults', { count: String(filteredProducts.length) })
                  : t('products.subtitle')}
              </Text>
            </Box>

            {/* Mobile filter button */}
            {isMobile && (
              <Button
                variant="light"
                leftSection={<IconFilter size={18} />}
                onClick={openFilter}
              >
                {t('products.filter')}
              </Button>
            )}
          </Group>

          {/* Active filter badge */}
          {selectedCategory && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {t('products.showing')}:
              </Text>
              <Badge
                size="lg"
                variant="filled"
                rightSection={
                  <ActionIcon
                    size="xs"
                    color="white"
                    variant="transparent"
                    onClick={() => handleCategorySelect(null)}
                  >
                    <IconX size={12} />
                  </ActionIcon>
                }
              >
                {selectedCategoryName}
              </Badge>
            </Group>
          )}
        </Stack>

        <Grid gutter="xl">
          {/* Categories sidebar - Desktop */}
          {!isMobile && (
            <Grid.Col span={3}>
              <Paper
                p="md"
                radius="md"
                withBorder
                style={{
                  position: 'sticky',
                  top: 120,
                }}
              >
                <CategoriesSidebar />
              </Paper>
            </Grid.Col>
          )}

          {/* Products grid */}
          <Grid.Col span={isMobile ? 12 : 9}>
            {filteredProducts.length === 0 ? (
              <Paper p="xl" radius="md" withBorder ta="center">
                <Stack align="center" gap="md">
                  <IconPackage size={64} stroke={1} color="gray" />
                  <Title order={3} c="dimmed">
                    {t('products.noProducts')}
                  </Title>
                  <Text c="dimmed">
                    {selectedCategory
                      ? t('products.noCategoryProducts')
                      : t('products.emptyStore')}
                  </Text>
                  {selectedCategory && (
                    <Button
                      variant="light"
                      onClick={() => handleCategorySelect(null)}
                    >
                      {t('products.viewAll')}
                    </Button>
                  )}
                </Stack>
              </Paper>
            ) : (
              <Grid>
                {filteredProducts.map((product: Product) => (
                  <Grid.Col key={product.id} span={{ base: 12, xs: 6, md: 4 }}>
                    <ProductCard product={product} />
                  </Grid.Col>
                ))}
              </Grid>
            )}
          </Grid.Col>
        </Grid>
      </Container>
    </>
  );
}

